import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Disc3, LayoutDashboard, Music, PenLine, Building2, UploadCloud, LogOut, Sparkles, Coins, Handshake, Globe, BarChart3, LifeBuoy, Users, ShieldCheck, Megaphone, X, ScrollText } from "lucide-react";
import { Button } from "../components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/songs", label: "Songs", icon: Music, testid: "nav-songs" },
  { to: "/writers", label: "Writers", icon: PenLine, testid: "nav-writers" },
  { to: "/publishers", label: "Publishers", icon: Building2, testid: "nav-publishers" },
  { to: "/royalties", label: "Royalties", icon: Coins, testid: "nav-royalties" },
  { to: "/deals", label: "Deal Room", icon: Handshake, testid: "nav-deals" },
  { to: "/rights", label: "Rights Map", icon: Globe, testid: "nav-rights" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/ai", label: "AI Assistant", icon: Sparkles, testid: "nav-ai" },
  { to: "/upload", label: "Import Catalog", icon: UploadCloud, testid: "nav-upload" },
  { to: "/support", label: "Support", icon: LifeBuoy, testid: "nav-support" },
];

const ADMIN_NAV = [
  { to: "/admin/users", label: "Users", icon: Users, testid: "nav-admin-users" },
  { to: "/admin/deals", label: "Deal Offers", icon: Handshake, testid: "nav-admin-deals" },
  { to: "/admin/support", label: "Support Desk", icon: LifeBuoy, testid: "nav-admin-support" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, testid: "nav-admin-audit" },
];

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
    isActive ? "bg-primary/10 text-primary border border-primary/30" : "text-zinc-400 hover:text-zinc-100 hover:bg-secondary border border-transparent"
  }`;

export default function Layout() {
  const { user, logout, setUser } = useAuth();
  const [ann, setAnn] = useState(null);
  const [notif, setNotif] = useState(0);

  useEffect(() => {
    if (!user) return;
    api.get("/announcements").then(({ data }) => {
      if (data.length) {
        const latest = data[0];
        if (localStorage.getItem("dismissed_ann") !== latest.id) setAnn(latest);
      }
    }).catch(() => {});
    const loadNotif = () => api.get("/notifications").then(({ data }) => setNotif(data.count)).catch(() => {});
    loadNotif();
    const iv = setInterval(loadNotif, 30000);
    const onFocus = () => loadNotif();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [user]);

  const stopImpersonate = async () => {
    try { const { data } = await api.post("/auth/stop-impersonate"); setUser(data); window.location.href = "/admin/users"; }
    catch { window.location.href = "/dashboard"; }
  };
  const dismissAnn = () => { if (ann) localStorage.setItem("dismissed_ann", ann.id); setAnn(null); };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="w-64 shrink-0 border-r border-border flex flex-col fixed h-screen bg-[#0b0b0d] z-20">
        <div className="px-6 py-6 border-b border-border flex items-center gap-2">
          <Disc3 className="h-6 w-6 text-primary" />
          <div className="leading-none">
            <div className="font-heading font-extrabold text-sm tracking-tight">xyz</div>
            <div className="font-heading font-extrabold text-sm tracking-tight -mt-0.5">Publishing OS</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} data-testid={testid} className={linkClass}>
              <Icon className="h-4 w-4" />
              <span className="flex-1">{label}</span>
              {to === "/support" && notif > 0 && (
                <span data-testid="support-notif-badge" className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center">{notif}</span>
              )}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <div className="pt-4 mt-4 border-t border-border">
              <div className="px-3 pb-2 text-[10px] font-semibold tracking-[0.25em] uppercase text-primary/70 flex items-center gap-1.5">
                <ShieldCheck className="h-3 w-3" /> Admin
              </div>
              {ADMIN_NAV.map(({ to, label, icon: Icon, testid }) => (
                <NavLink key={to} to={to} data-testid={testid} className={linkClass}>
                  <Icon className="h-4 w-4" />{label}
                </NavLink>
              ))}
            </div>
          )}
        </nav>
        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center text-primary font-bold text-sm shrink-0">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name || "User"}</div>
              <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
            </div>
          </div>
          <Button variant="outline" size="sm" data-testid="logout-btn" onClick={logout} className="w-full border-border bg-secondary hover:bg-zinc-800 text-zinc-300">
            <LogOut className="h-4 w-4 mr-2" /> Log out
          </Button>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-h-screen">
        {user?.is_impersonating && (
          <div data-testid="impersonation-banner" className="bg-amber-400 text-black px-8 py-2.5 flex items-center justify-between text-sm font-semibold">
            <span>Acting as {user.email} — you are viewing this account as an admin.</span>
            <button data-testid="stop-impersonate-btn" onClick={stopImpersonate} className="underline hover:no-underline">Exit to admin</button>
          </div>
        )}
        {ann && (
          <div data-testid="announcement-banner" className="bg-primary/10 border-b border-primary/30 px-8 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm"><Megaphone className="h-4 w-4 text-primary" /><span className="font-semibold">{ann.title}</span><span className="text-zinc-400">{ann.body}</span></div>
            <button onClick={dismissAnn} data-testid="dismiss-announcement" className="text-zinc-400 hover:text-zinc-100"><X className="h-4 w-4" /></button>
          </div>
        )}
        <div className="p-8 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
